// Document Protection Layer - Anti-copy/screenshot features
import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Shield } from 'lucide-react';

export function DocumentProtection({ 
  documentId, 
  userEmail, 
  userName,
  isOwner = false,
  showBadge = true,
  hideWatermark = false,
  children 
}) {
  const [settings, setSettings] = useState({
    copy_protection: true,
    watermark: true,
    logging: true
  });

  useEffect(() => {
    loadSettings();
    
    if (!isOwner) {
      // Enable protections for non-owners
      enableCopyProtection();
      return () => {
        disableCopyProtection();
      };
    }
  }, [documentId, isOwner]);

  const loadSettings = async () => {
    try {
      const response = await api.request(`/security/document/${documentId}/settings`);
      setSettings(response.settings);
    } catch (err) {
      console.error('Failed to load security settings:', err);
    }
  };

  const logAttempt = async (attemptType) => {
    if (!settings.logging) return;
    
    try {
      await api.request('/security/log-copy-attempt', {
        method: 'POST',
        body: JSON.stringify({
          document_id: documentId,
          attempt_type: attemptType
        })
      });
    } catch (err) {
      console.error('Failed to log attempt:', err);
    }
  };

  const enableCopyProtection = () => {
    if (!settings.copy_protection) return;

    // Prevent right-click
    document.addEventListener('contextmenu', handleContextMenu);
    
    // Prevent keyboard shortcuts
    document.addEventListener('keydown', handleKeyDown);
    
    // Prevent text selection
    document.addEventListener('selectstart', handleSelectStart);
    
    // Detect screenshot attempts (limited detection)
    document.addEventListener('keyup', handleScreenshotAttempt);
    
    // Prevent printing
    window.addEventListener('beforeprint', handlePrint);
  };

  const disableCopyProtection = () => {
    document.removeEventListener('contextmenu', handleContextMenu);
    document.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('selectstart', handleSelectStart);
    document.removeEventListener('keyup', handleScreenshotAttempt);
    window.removeEventListener('beforeprint', handlePrint);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    showWarning('Right-click is disabled to protect this content.');
    logAttempt('right-click');
    return false;
  };

  const handleKeyDown = (e) => {
    // Prevent Ctrl+C, Ctrl+A, Ctrl+P, Ctrl+S, Ctrl+U
    if ((e.ctrlKey || e.metaKey) && ['c', 'a', 'p', 's', 'u'].includes(e.key.toLowerCase())) {
      e.preventDefault();
      showWarning('Copy/paste is disabled to protect this content.');
      logAttempt('keyboard-shortcut');
      return false;
    }
    
    // Prevent F12, Ctrl+Shift+I (DevTools)
    if (e.key === 'F12' || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I')) {
      e.preventDefault();
      return false;
    }
  };

  const handleSelectStart = (e) => {
    // Allow selection in input fields
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return true;
    }
    
    e.preventDefault();
    logAttempt('text-selection');
    return false;
  };

  const handleScreenshotAttempt = (e) => {
    // Detect PrintScreen key (limited - can't fully prevent)
    if (e.key === 'PrintScreen') {
      logAttempt('screenshot-attempt');
      showWarning('Screenshots of protected content are logged and may violate terms of use.');
    }
  };

  const handlePrint = (e) => {
    e.preventDefault();
    logAttempt('print-attempt');
    showWarning('Printing is disabled to protect this content.');
    return false;
  };

  const showWarning = (message) => {
    // Create temporary warning toast
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ef4444;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-size: 14px;
      max-width: 300px;
      animation: slideIn 0.3s ease;
    `;
    toast.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
        </svg>
        <span>${message}</span>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  };

  return (
    <div className="relative">
      {/* Watermark overlay for non-owners */}
      {!isOwner && settings.watermark && !hideWatermark && (
        <div 
          className="pointer-events-none fixed inset-0 z-50"
          style={{
            background: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 200px,
              rgba(0, 0, 0, 0.02) 200px,
              rgba(0, 0, 0, 0.02) 201px
            )`,
            mixBlendMode: 'multiply'
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center opacity-5">
            <div className="transform -rotate-45 text-6xl font-bold text-gray-800 select-none whitespace-nowrap">
              {userName || userEmail} • {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      )}

      {/* Protection indicator */}
      {!isOwner && settings.copy_protection && showBadge && (
        <div className="fixed bottom-4 left-4 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm flex items-center gap-2 text-xs text-gray-600 z-40">
          <Shield className="h-4 w-4 text-green-600" />
          <span>Content Protected</span>
        </div>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
        
        /* Disable text selection for protected content */
        .protected-content {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
      `}</style>

      {/* Render children */}
      <div className={!isOwner && settings.copy_protection ? 'protected-content' : ''}>
        {children}
      </div>
    </div>
  );
}
